/**
 * `<WaterSurface>` — the React face of the water engine.
 *
 * Used at two very different scales: full-bleed behind Horizon, and 38px wide as
 * Ledger's edge gauge. Same engine, same shader, one component.
 *
 * D-4 — decoration only. This renders `aria-hidden`, contributes nothing to the
 * accessibility tree, and the figures it sits behind are readable without it.
 */

import { useEffect, useRef, useState } from "react";

import { bridge } from "@/native/impl";
import { startTilt } from "@/native/tilt";
import { CssWater } from "./css_water";
import { type WaterEngine, createWater } from "./water_engine";

export interface WaterSurfaceProps {
  /** 0..1. Above 1 the water floods past the top — the over-budget state. */
  level: number;
  /** Bump to throw a ripple: pass a counter that increments on each logged expense. */
  rippleKey?: number;
  /** Paints the water in `--color-over` instead of the accent. */
  isOver?: boolean;
  /** Capture's rise preview: where the amount being typed would take the level, or null. */
  preview?: number | null;
  className?: string;
  /**
   * Positioning for the host element. Horizon uses this to inset the canvas above
   * its action band, which cannot be a static class because the band is measured.
   * Merged *under* the over-budget colour tokens, so a caller cannot accidentally
   * repaint a flooded sea.
   */
  style?: React.CSSProperties;
}

export function WaterSurface({
  level,
  rippleKey = 0,
  isOver = false,
  preview = null,
  className,
  style,
}: WaterSurfaceProps): React.JSX.Element {
  // Which tier the engine could start. "static" means no drawing context at
  // all, and the CSS water stands in rather than leaving a blank (D-5).
  const [tier, setTier] = useState<WaterEngine["tier"] | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const hostRef = useRef<HTMLDivElement>(null);
  const engineRef = useRef<WaterEngine | null>(null);
  const lastRipple = useRef(rippleKey);

  useEffect(() => {
    const canvas = canvasRef.current;
    const host = hostRef.current;
    if (!canvas || !host) return;

    const engine = createWater(canvas, host, level, setTier);
    engineRef.current = engine;
    setTier(engine.tier);
    let disposed = false;
    let stopTilt = (): void => undefined;
    const openedAt = Date.now();
    if (bridge.platform === "android") void bridge.powerSave().then((batterySaver) => {
      if (!disposed) stopTilt = startTilt(bridge.platform, (slope) => engine.setTilt(slope), window, batterySaver, openedAt);
    }).catch((cause: unknown) => { console.warn("Could not check motion power preference:", cause); });
    return () => {
      disposed = true;
      stopTilt();
      engine.destroy();
      engineRef.current = null;
    };
    // Mounted once. `level` is applied through the effect below so a change
    // animates rather than rebuilding the GL context.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const ripple = rippleKey !== lastRipple.current;
    lastRipple.current = rippleKey;
    engineRef.current?.setLevel(level, ripple);
  }, [level, rippleKey]);

  useEffect(() => {
    engineRef.current?.setPreview(preview);
  }, [preview]);

  // Follow the theme when the OS switches, or the user toggles it.
  useEffect(() => {
    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = (): void => engineRef.current?.refreshColors();
    media.addEventListener("change", onChange);

    const observer = new MutationObserver(onChange);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });

    return () => {
      media.removeEventListener("change", onChange);
      observer.disconnect();
    };
  }, []);

  return (
    <div
      ref={hostRef}
      aria-hidden="true"
      data-over={isOver ? "true" : undefined}
      className={className}
      style={
        isOver
          ? ({
              ...style,
              // Semantic colour replaces the accent when flooded (D-8), without
              // the shader needing to know what "over budget" means. Tokens from
              // the palette, so the flood follows the theme like everything else.
              "--ae-crest": "var(--ae-over)",
              "--ae-water": "var(--ae-water-over)",
            } as React.CSSProperties)
          : style
      }
    >
      <canvas ref={canvasRef} className={tier === "static" ? "hidden" : "block h-full w-full"} />
      {tier === "static" ? <CssWater level={level} preview={preview} className="h-full w-full" /> : null}
    </div>
  );
}
